/**
 * Utility functions for HangarCanvas component
 * Pure functions for geometry, SVG conversion, and magnetic line calculations
 */

import type { Polygon, LineString, Position } from "geojson";
import type { ComponentGeometry, ObstructionType, HangarDoor, DoorWall } from "../types";

// ============================================================================
// Color Palettes and Constants
// ============================================================================

/** Color palette for aircraft visualization */
export const AIRCRAFT_COLORS = [
  "#4CAF50", // Green
  "#2196F3", // Blue
  "#FF9800", // Orange
  "#9C27B0", // Purple
  "#F44336", // Red
  "#00BCD4", // Cyan
  "#795548", // Brown
  "#607D8B", // Blue Grey
];

/** Component color palette for 3D visualization */
export const COMPONENT_COLORS = {
  wing: { fill: "#81C784", stroke: "#4CAF50" },           // Green
  tail: { fill: "#64B5F6", stroke: "#2196F3" },           // Blue
  body: { fill: "#FFB74D", stroke: "#FF9800" },           // Orange
  verticalTailFin: { fill: "#BA68C8", stroke: "#9C27B0" }, // Purple
};

/** Locked aircraft indicator color (gold) */
export const LOCKED_COLOR = "#FFD700";

/** Obstruction style palette by type */
export const OBSTRUCTION_STYLES: Record<ObstructionType, { fill: string; stroke: string; dasharray?: string }> = {
  OBSTRUCTION: { fill: "rgba(100, 100, 100, 0.6)", stroke: "#333" },
  NO_TOW_AREA: { fill: "rgba(244, 67, 54, 0.15)", stroke: "#F44336", dasharray: "5,3" },
  UNOBSTRUCTIVE: { fill: "rgba(33, 150, 243, 0.3)", stroke: "#2196F3", dasharray: "3,2" },
  CAUTION: { fill: "rgba(255, 193, 7, 0.4)", stroke: "#FFC107", dasharray: "4,2" },
};

/** Movable obstacle (GSE) style - orange to distinguish from static obstructions */
export const MOVABLE_OBSTACLE_STYLE = {
  fill: "rgba(255, 165, 0, 0.3)",
  stroke: "#ff8c00",
  strokeWidth: 1,
  dasharray: "4,2",
  labelColor: "#cc7000",
};

/** Multi-door color palette by wall */
export const DOOR_COLORS: Record<'south' | 'east' | 'north' | 'west', string> = {
  south: '#4CAF50',  // Green - primary/front door
  north: '#2196F3',  // Blue - back door
  east: '#FF9800',   // Orange - right side door
  west: '#9C27B0',   // Purple - left side door
};

// ============================================================================
// SVG Path Conversion Functions
// ============================================================================

/**
 * Convert a GeoJSON Polygon to an SVG path string
 * Note: SVG Y-axis is inverted (0 at top), so we flip Y coordinates
 */
export function polygonToSvgPath(polygon: Polygon, hangarDepth: number): string {
  const ring = polygon.coordinates[0];
  if (!ring || ring.length === 0) return "";

  const points = ring.map(([x, y]) => {
    // Flip Y coordinate for SVG (hangarDepth - y)
    const svgY = hangarDepth - y;
    return `${x.toFixed(2)},${svgY.toFixed(2)}`;
  });

  return `M ${points.join(" L ")} Z`;
}

/**
 * Convert a GeoJSON LineString to SVG path string
 */
export function lineStringToSvgPath(line: LineString, hangarDepth: number): string {
  const coords = line.coordinates;
  if (!coords || coords.length < 2) return "";

  const points = coords.map(([x, y]) => {
    const svgY = hangarDepth - y;
    return `${x.toFixed(2)},${svgY.toFixed(2)}`;
  });

  return `M ${points.join(" L ")}`;
}

/**
 * Convert an array of Position coordinates to SVG path string (for curved tow paths)
 */
export function positionsToSvgPath(positions: Position[], hangarDepth: number): string {
  if (!positions || positions.length < 2) return "";

  const points = positions.map(([x, y]) => {
    const svgY = hangarDepth - y;
    return `${x.toFixed(2)},${svgY.toFixed(2)}`;
  });

  return `M ${points.join(" L ")}`;
}

/**
 * Convert a component geometry (Polygon or MultiPolygon) to SVG path string
 */
export function geometryToSvgPath(geom: ComponentGeometry, hangarDepth: number): string {
  if (geom.type === "Polygon") {
    return polygonToSvgPath(geom, hangarDepth);
  }
  // MultiPolygon - combine all polygon paths
  const paths = geom.coordinates.map((polyCoords) => {
    const ring = polyCoords[0];
    if (!ring || ring.length === 0) return "";
    const points = ring.map(([x, y]) => {
      const svgY = hangarDepth - y;
      return `${x.toFixed(2)},${svgY.toFixed(2)}`;
    });
    return `M ${points.join(" L ")} Z`;
  });
  return paths.filter(Boolean).join(" ");
}

// ============================================================================
// Centroid Calculation Functions
// ============================================================================

/**
 * Get the centroid of a polygon for label placement
 */
export function getPolygonCentroid(
  polygon: Polygon,
  hangarDepth: number
): { x: number; y: number } {
  const ring = polygon.coordinates[0];
  if (!ring || ring.length === 0) return { x: 0, y: 0 };

  let sumX = 0;
  let sumY = 0;
  for (const [x, y] of ring) {
    sumX += x;
    sumY += hangarDepth - y; // Flip Y for SVG
  }

  return {
    x: sumX / ring.length,
    y: sumY / ring.length,
  };
}

/**
 * Get centroids of a component geometry
 * For Polygon: returns single centroid
 * For MultiPolygon: returns centroid of each polygon (e.g., left wing, right wing)
 */
export function getGeometryCentroids(
  geom: ComponentGeometry,
  hangarDepth: number
): Array<{ x: number; y: number }> {
  if (geom.type === "Polygon") {
    const ring = geom.coordinates[0];
    if (!ring || ring.length === 0) return [];
    let sumX = 0;
    let sumY = 0;
    for (const [x, y] of ring) {
      sumX += x;
      sumY += hangarDepth - y;
    }
    return [{ x: sumX / ring.length, y: sumY / ring.length }];
  } else {
    // MultiPolygon - return centroid of each polygon part
    const centroids: Array<{ x: number; y: number }> = [];
    for (const poly of geom.coordinates) {
      const ring = poly[0];
      if (!ring || ring.length === 0) continue;
      let sumX = 0;
      let sumY = 0;
      for (const [x, y] of ring) {
        sumX += x;
        sumY += hangarDepth - y;
      }
      centroids.push({ x: sumX / ring.length, y: sumY / ring.length });
    }
    return centroids;
  }
}

// ============================================================================
// Magnetic Line Helper Functions (m0/m29 Dual Reference System)
// ============================================================================

/** Distance from back wall within which m0 is used (feet) */
export const M0_DISTANCE_FROM_BACK_WALL = 50;

/** Buffer multiplier for door proximity checks */
export const DOOR_BUFFER_MULTIPLIER = 1.25;

/** Coefficient for m29 depth (from Peter's heatmap analysis) */
export const M29_DEPTH_COEFFICIENT = 2.9;

// Re-export for direct usage
export { M29_DEPTH_COEFFICIENT as M29_COEFF };

export type MagneticRefType = 'm0' | 'm29';

export interface HangarWithDoors {
  width: number;
  depth: number;
  leftDoor?: number;
  rightDoor?: number;
}

/**
 * Get m0 (front/door) magnetic reference point.
 * Located at hangar centerline on the door line (Y=0).
 */
export function getM0ReferencePoint(hangar: { width: number }) {
  return { x: hangar.width / 2, y: 0 };
}

/**
 * Get m29 (deep) magnetic reference point.
 * Located at hangar centerline, 2.9x depth south of door.
 */
export function getM29ReferencePoint(hangar: { width: number; depth: number }) {
  return { x: hangar.width / 2, y: -M29_DEPTH_COEFFICIENT * hangar.depth };
}

/**
 * Get m0 reference point relative to a specific door.
 * Returns the center of the door opening, offset outward from the hangar.
 */
export function getM0ReferencePointForDoor(
  door: HangarDoor,
  hangar: { width: number; depth: number }
): { x: number; y: number } {
  const doorCenterX = door.position + door.width / 2;
  const doorCenterY = door.position + door.width / 2;

  switch (door.wall) {
    case 'south':
      // South door: center of door at Y=0
      return { x: doorCenterX, y: 0 };
    case 'north':
      // North door: center of door at Y=depth
      return { x: doorCenterX, y: hangar.depth };
    case 'east':
      // East door: center of door at X=width
      return { x: hangar.width, y: doorCenterY };
    case 'west':
      // West door: center of door at X=0
      return { x: 0, y: doorCenterY };
    default:
      // Fallback to south door behavior
      return { x: hangar.width / 2, y: 0 };
  }
}

/**
 * Get m29 (deep) reference point relative to a specific door.
 * Located 2.9x depth away from the door, perpendicular to the wall.
 */
export function getM29ReferencePointForDoor(
  door: HangarDoor,
  hangar: { width: number; depth: number }
): { x: number; y: number } {
  const m0 = getM0ReferencePointForDoor(door, hangar);
  const deepOffset = M29_DEPTH_COEFFICIENT * hangar.depth;

  switch (door.wall) {
    case 'south':
      // South door: offset in -Y direction (outside hangar)
      return { x: m0.x, y: -deepOffset };
    case 'north':
      // North door: offset in +Y direction (outside hangar)
      return { x: m0.x, y: hangar.depth + deepOffset };
    case 'east':
      // East door: offset in +X direction (outside hangar)
      return { x: hangar.width + deepOffset, y: m0.y };
    case 'west':
      // West door: offset in -X direction (outside hangar)
      return { x: -deepOffset, y: m0.y };
    default:
      // Fallback to south door behavior
      return { x: hangar.width / 2, y: -deepOffset };
  }
}

/**
 * Select reference point for an aircraft based on its assigned door.
 * Uses position-aware logic relative to the door's wall.
 */
export function selectReferencePointForDoor(
  position: { x: number; y: number },
  door: HangarDoor,
  hangar: { width: number; depth: number }
): { point: { x: number; y: number }; type: MagneticRefType } {
  // Calculate distance to the door's wall
  let distanceToWall: number;
  switch (door.wall) {
    case 'south':
      distanceToWall = position.y;
      break;
    case 'north':
      distanceToWall = hangar.depth - position.y;
      break;
    case 'east':
      distanceToWall = hangar.width - position.x;
      break;
    case 'west':
      distanceToWall = position.x;
      break;
    default:
      distanceToWall = position.y;
  }

  // If near the door's wall, use m0; otherwise use m29
  if (distanceToWall < M0_DISTANCE_FROM_BACK_WALL) {
    return { point: getM0ReferencePointForDoor(door, hangar), type: 'm0' };
  }

  return { point: getM29ReferencePointForDoor(door, hangar), type: 'm29' };
}

/**
 * Position-aware magnetic reference point selection.
 * Returns m0 near walls/doors, m29 in main hangar body.
 */
export function selectReferencePoint(
  position: { x: number; y: number },
  hangar: HangarWithDoors
): { point: { x: number; y: number }; type: MagneticRefType } {
  const { width, depth, leftDoor, rightDoor } = hangar;

  // Check if near back wall (within 50ft)
  if (position.y > depth - M0_DISTANCE_FROM_BACK_WALL) {
    return { point: getM0ReferencePoint(hangar), type: 'm0' };
  }

  // Check if near left door
  if (leftDoor != null && position.x <= leftDoor * DOOR_BUFFER_MULTIPLIER) {
    return { point: getM0ReferencePoint(hangar), type: 'm0' };
  }

  // Check if near right door
  if (rightDoor != null && position.x >= width - rightDoor * DOOR_BUFFER_MULTIPLIER) {
    return { point: getM0ReferencePoint(hangar), type: 'm0' };
  }

  // Default: main body uses m29
  return { point: getM29ReferencePoint(hangar), type: 'm29' };
}

/**
 * @deprecated Use selectReferencePoint for position-aware selection.
 * Legacy: Get the magnetic reference point for a hangar.
 */
export function getMagneticReferencePoint(hangar: { width: number; depth: number }) {
  return { x: hangar.width / 2, y: hangar.depth * -0.2 };
}

/**
 * Calculate ideal angle from position to reference point.
 * Returns angle in aircraft heading convention:
 * - 0° = +Y (nose toward back wall)
 * - 90° = -X (nose toward left)
 * - 180° = -Y (nose toward door)
 * - 270° = +X (nose toward right)
 */
export function calculateIdealAngleToPoint(
  position: { x: number; y: number },
  referencePoint: { x: number; y: number }
): number {
  const dx = referencePoint.x - position.x;
  const dy = referencePoint.y - position.y;
  // Negate dx to convert from standard math convention to aircraft heading convention
  const radians = Math.atan2(-dx, dy);
  let degrees = (radians * 180) / Math.PI;
  if (degrees < 0) degrees += 360;
  return degrees;
}

/**
 * Convert angle (degrees) to unit vector.
 * Uses counter-clockwise convention (matches backend/rotatePolygon):
 * 0° = +Y direction (nose toward back of hangar)
 * 90° = -X direction (nose toward left)
 * 180° = -Y direction (nose toward door)
 * 270° = +X direction (nose toward right)
 */
export function angleToVector(angleDegrees: number) {
  const radians = (angleDegrees * Math.PI) / 180;
  return { x: -Math.sin(radians), y: Math.cos(radians) };
}

/**
 * Calculate the tow angle for an aircraft to exit toward the door (Y=0).
 * Chooses nose or tail direction based on which points more toward the door.
 * @param aircraftAngle The aircraft's heading angle (0° = nose toward +Y)
 * @returns The tow angle that moves the aircraft toward the door
 */
export function calculateTowAngle(aircraftAngle: number): number {
  const noseYComponent = Math.cos((aircraftAngle * Math.PI) / 180);
  // If nose points toward door (negative Y), tow forward; otherwise tow backward
  return noseYComponent < 0 ? aircraftAngle : (aircraftAngle + 180) % 360;
}
